import classNames from "classnames";
import { useId, type FC } from "react";
import { useCollapse } from "react-collapsed";
import {
  stateIsCurrent,
  stateIsActive,
  stateIsDisabled,
  stateIsDone,
  stateIsWarning,
} from "~/services/navigation/navState";
import { translations } from "~/services/translations/translations";
import { NavigationList } from "./NavigationList";
import { type NavItem } from "./types";
import { Icon } from "../common/Icon";

type StateIconProps = {
  id: string;
  isDone: boolean;
  showWarningIcon?: boolean;
};

const StateIcon: FC<StateIconProps> = ({ id, isDone, showWarningIcon }) => {
  if (isDone) {
    return (
      <Icon
        name="check-circle"
        id={id}
        className="fill-kern-feedback-success"
        aria-label={translations.navigation.navigationItemFinished.de}
        size={24}
      />
    );
  } else if (showWarningIcon) {
    return (
      <Icon
        name="warning"
        id={id}
        className="fill-kern-feedback-warning"
        aria-label={translations.navigation.navigationItemWarning.de}
        size={24}
      />
    );
  }
  return undefined;
};

const DEFAULT_EMPTY_SUBFLOWS: NavItem[] = [];

export function NavItem({
  destination,
  label,
  state,
  subflows = DEFAULT_EMPTY_SUBFLOWS,
  forceExpanded,
  isChild = false,
  isFirst = false,
  isLast = false,
  firstItemRef,
  classNameNavigationList,
}: Readonly<
  NavItem & {
    isChild?: boolean;
    isFirst?: boolean;
    isLast?: boolean;
    firstItemRef?: React.RefObject<HTMLAnchorElement | null>;
    classNameNavigationList?: string;
  }
>) {
  const visibleChildItems = subflows.filter((subItem) =>
    stateIsActive(subItem.state),
  );
  const hasSubflows = visibleChildItems.length > 0;
  const isDisabled = stateIsDisabled(state);
  const isCurrent = stateIsCurrent(state);
  const isDone = stateIsDone(state);
  const isWarning = stateIsWarning(state);
  const collapse = useCollapse({
    defaultExpanded: forceExpanded ?? isCurrent,
  });

  // Transparent last: borders to avoid layout shifts
  const liClassNames = classNames(
    "flex w-full flex-col list-none",
    "border-b border-kern-neutral-200 last:border-0",
    "relative focus-within:z-10",
    {
      "text-kern-neutral-400! cursor-not-allowed hover:font-normal pointer-events-none":
        isDisabled,
      "border-none": isChild,
    },
  );

  const itemClassNames = classNames(
    "kern-body kern-body--small w-full p-16! flex justify-between items-center",
    "hover:underline hover:bg-kern-neutral-200",
    "active:bg-kern-neutral-200",
    "relative",
    "focus-visible:outline-none",
    "focus-visible:bg-white",
    "focus-visible:rounded-[var(--kern-metric-border-radius-default)]",
    "focus-visible:shadow-[0_0_0_2px_var(--kern-color-action-on-default),0_0_0_4px_var(--kern-color-action-focus-default),0_0_0_6px_var(--kern-color-action-focus-default)]",
    {
      "rounded-t": isFirst && !isChild,
      "rounded-b": isLast && !isChild,
      "kern-alert--warning hover:bg-kern-orange-100!": isWarning,
      "kern-body--bold bg-kern-neutral-100": isCurrent && !hasSubflows,
      "pl-24!": isChild,
      "text-kern-neutral-400! cursor-not-allowed hover:font-normal pointer-events-none":
        isDisabled,
    },
  );
  const iconId = useId();

  const statusText = isDone
    ? translations.navigation.navigationItemFinished.de
    : isWarning
      ? translations.navigation.navigationItemWarning.de
      : "";

  return (
    <li className={liClassNames}>
      {hasSubflows ? (
        <>
          <button
            className={itemClassNames}
            aria-disabled={isDisabled}
            aria-expanded={collapse.isExpanded}
            {...collapse.getToggleProps()}
            aria-describedby={isDone || isWarning ? iconId : undefined}
            // oxlint-disable-next-line aria-role
            role={undefined} // due the rest operator, the role is assigned to the button in the server side rendering
          >
            <span>{label}</span>
            <div className="flex items-center gap-8 justify-end align-end self-end">
              <Icon
                name={
                  collapse.isExpanded
                    ? "keyboard-arrow-up"
                    : "keyboard-arrow-down"
                }
                className="ml-auto"
              />
              <StateIcon
                id={iconId}
                isDone={isDone}
                showWarningIcon={isWarning}
              />
            </div>
          </button>
          {
            // due the rest operator, the role is assigned to the section in the server side rendering
          }
          <section
            {...collapse.getCollapseProps()}
            // oxlint-disable-next-line jsx-a11y/aria-role
            role={undefined}
          >
            <NavigationList
              navItems={visibleChildItems}
              isChild={true}
              className={classNameNavigationList}
            />
          </section>
        </>
      ) : (
        <a
          href={destination}
          className={itemClassNames}
          aria-disabled={isDisabled}
          aria-current={isCurrent}
          ref={firstItemRef}
          data-testid={"nav-item-link"}
          aria-describedby={isDone || isWarning ? iconId : undefined}
          aria-label={`${label}${statusText ? `, ${statusText}` : ""}`}
        >
          <span>{label}</span>
          <StateIcon id={iconId} isDone={isDone} showWarningIcon={isWarning} />
        </a>
      )}
    </li>
  );
}
